import { createFileRoute, notFound, redirect } from '@tanstack/react-router';
import { z } from 'zod';

import { useBesluitId } from '@/api/queries/besluit/besluit';

import { useHoofdstukkenWithLabels } from './-hooks/useHoofdstukkenWithLabels';
import { useEditorStore } from './-store/useEditorStore';
import { useLabelEditorStore } from './-store/useLabelEditorStore';
import { fetchHoofdstukkenInEditorQueryData } from './-utils/useHoofdstukkenInEditor';

const searchSchema = z.object({
  type: z.enum(['first-hoofdstuk', 'first-label-hoofdstuk']),
  labelIds: z.array(z.string()).optional(),
});

export const Route = createFileRoute(
  '/$bedrijfLineageId/editor/$besluitId/redirect'
)({
  validateSearch: searchSchema.parse,
  beforeLoad: async ({
    context: { queryClient },
    params: { besluitId, bedrijfLineageId },
    search,
  }) => {
    const besluit = await queryClient.fetchQuery(
      useBesluitId.getOptions(besluitId)
    );

    if (!besluit) throw notFound();

    switch (search.type) {
      case 'first-hoofdstuk': {
        const grondslagen = await fetchHoofdstukkenInEditorQueryData({
          besluitId,
          queryClient,
        });
        const firstHoofdstuk = grondslagen[0]?.hoofdstukken[0];

        /**
         * If there's a first hoofdstuk, redirect to it
         */
        if (firstHoofdstuk) {
          throw redirect({
            to: '/$bedrijfLineageId/editor/$besluitId/voorschriften/$hoofdstukLineageId',
            params: {
              bedrijfLineageId,
              besluitId,
              hoofdstukLineageId: firstHoofdstuk.Lineage,
            },
          });
        } else {
          throw redirect({
            to: '/$bedrijfLineageId/editor/$besluitId/voorschriften/hoofdstuk',
            params: {
              bedrijfLineageId,
              besluitId,
            },
          });
        }
      }

      case 'first-label-hoofdstuk': {
        useLabelEditorStore.getState().setFilter({
          type: 'labels',
          labelIds: search.labelIds ?? [],
        });
        useEditorStore.getState().setSidebarLeftOpen(true);

        if (!search.labelIds?.length) {
          throw redirect({
            to: '/$bedrijfLineageId/editor/$besluitId/besluittekst',
            params: {
              bedrijfLineageId,
              besluitId,
            },
          });
        }

        const hoofdstukkenResponse = await queryClient.fetchQuery(
          useHoofdstukkenWithLabels.getOptions({
            type: 'labels',
            labelIds: search.labelIds,
            besluitId,
          })
        );
        const firstHoofdstuk = hoofdstukkenResponse.objects?.[0];

        if (!firstHoofdstuk) {
          throw redirect({
            to: '/$bedrijfLineageId/editor/$besluitId/besluittekst',
            params: {
              bedrijfLineageId,
              besluitId,
            },
          });
        }

        throw redirect({
          to: '/$bedrijfLineageId/editor/$besluitId/labels/$hoofdstukLineageId',
          params: {
            bedrijfLineageId,
            besluitId,
            hoofdstukLineageId: firstHoofdstuk.Lineage,
          },
        });
      }
    }
  },
});
